import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { Textarea } from "../../ui/textarea";
import { AdminImageUploadField } from "./AdminImageUploadField";

export const AdminQcmQuestionEditor = ({ qcmId, question, questionIndex, canRemoveQuestion, isUploading, onUpdateQuestion, onRemoveQuestion, onAddAnswer, onUpdateAnswer, onRemoveAnswer, onSelectCorrectAnswer, onUploadQuestionImage }) => {
  return (
    <div className="rounded-[1.8rem] border border-[#d9e7d5] bg-white p-5 shadow-[0_20px_50px_-42px_rgba(15,118,110,0.18)]" data-testid={`admin-qcm-question-card-${question.id}`}>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0f766e]" data-testid={`admin-qcm-question-index-${question.id}`}>Question {questionIndex + 1}</div>
          <div className="mt-2 text-sm text-slate-500">L’ordre d’affichage est mélangé automatiquement côté public.</div>
        </div>
        <Button className="rounded-full border-slate-200 text-slate-600 hover:text-slate-900 disabled:opacity-50" data-testid={`admin-qcm-remove-question-button-${question.id}`} disabled={!canRemoveQuestion} onClick={() => onRemoveQuestion(question.id)} type="button" variant="outline">
          <Trash2 className="mr-2 h-4 w-4" />
          Supprimer la question
        </Button>
      </div>

      <div className="mt-5 space-y-2">
        <Label htmlFor={`admin-qcm-question-text-${question.id}`}>Énoncé de la question</Label>
        <Input data-testid={`admin-qcm-question-text-input-${question.id}`} id={`admin-qcm-question-text-${question.id}`} onChange={(event) => onUpdateQuestion(question.id, { questionText: event.target.value })} value={question.questionText} />
      </div>

      <div className="mt-4 space-y-2">
        <Label htmlFor={`admin-qcm-question-explanation-${question.id}`}>Explication (visible dans la correction)</Label>
        <Textarea
          data-testid={`admin-qcm-question-explanation-input-${question.id}`}
          id={`admin-qcm-question-explanation-${question.id}`}
          onChange={(event) => onUpdateQuestion(question.id, { explanation: event.target.value })}
          placeholder="Courte explication affichée à l'utilisateur après le test."
          rows={2}
          value={question.explanation || ""}
        />
      </div>

      <div className="mt-6">
        <AdminImageUploadField
          fallbackText="Aucune image définie pour cette question."
          imageUrl={question.imageUrl}
          inputId={`admin-qcm-question-image-url-${question.id}`}
          inputTestId={`admin-qcm-question-image-url-input-${question.id}`}
          isUploading={isUploading}
          label="Image optionnelle de la question (URL)"
          onChange={(value) => onUpdateQuestion(question.id, { imageUrl: value })}
          onFileSelected={(file) => onUploadQuestionImage(question.id, file)}
          previewAlt={question.questionText}
          previewImageTestId={`admin-qcm-question-image-preview-${question.id}`}
          previewTestId={`admin-qcm-question-image-preview-wrapper-${question.id}`}
          uploadButtonTestId={`admin-qcm-question-image-upload-button-${question.id}`}
          wrapperTestId={`admin-qcm-question-image-field-${question.id}`}
        />
      </div>

      <div className="mt-6 rounded-[1.6rem] border border-[#d8ece4] bg-[#f8fcf3] p-5" data-testid={`admin-qcm-question-answers-block-${question.id}`}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="text-sm font-semibold uppercase tracking-[0.22em] text-[#0f766e]">Réponses possibles</div>
            <p className="mt-2 text-sm text-slate-600">Une seule bonne réponse suffit pour l’instant.</p>
          </div>
          <Button className="rounded-full bg-[#0f766e] text-white hover:bg-[#149289]" data-testid={`admin-qcm-add-answer-button-${question.id}`} onClick={() => onAddAnswer(question.id)} type="button">
            <Plus className="mr-2 h-4 w-4" />
            Ajouter une réponse
          </Button>
        </div>

        <RadioGroup className="mt-5 gap-4" data-testid={`admin-qcm-correct-answer-group-${question.id}`} onValueChange={(value) => onSelectCorrectAnswer(question.id, value)} value={question.correctAnswerId}>
          {question.answers.map((answer) => (
            <div className="rounded-[1.4rem] border border-white bg-white p-4 shadow-[0_16px_36px_-28px_rgba(15,118,110,0.22)]" data-testid={`admin-qcm-answer-row-${question.id}-${answer.id}`} key={answer.id}>
              <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-center">
                <div className="space-y-2">
                  <Label htmlFor={`admin-qcm-answer-input-${answer.id}`}>Texte de réponse</Label>
                  <Input data-testid={`admin-qcm-answer-input-${question.id}-${answer.id}`} id={`admin-qcm-answer-input-${answer.id}`} onChange={(event) => onUpdateAnswer(question.id, answer.id, event.target.value)} value={answer.text} />
                </div>
                <div className="flex items-center justify-between gap-4 md:justify-end">
                  <label className="inline-flex items-center gap-3 rounded-full border border-[#d8ece4] bg-[#f8fcf3] px-4 py-2 text-sm text-slate-700" data-testid={`admin-qcm-correct-answer-label-${question.id}-${answer.id}`} htmlFor={`admin-qcm-correct-answer-${answer.id}`}>
                    <RadioGroupItem data-testid={`admin-qcm-correct-answer-radio-${question.id}-${answer.id}`} id={`admin-qcm-correct-answer-${answer.id}`} value={answer.id} />
                    Bonne réponse
                  </label>
                  <Button className="rounded-full border-slate-200 text-slate-600 hover:text-slate-900 disabled:opacity-50" data-testid={`admin-qcm-remove-answer-button-${question.id}-${answer.id}`} disabled={question.answers.length <= 2} onClick={() => onRemoveAnswer(question.id, answer.id)} type="button" variant="outline">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </RadioGroup>
      </div>
    </div>
  );
};